import { Button } from '@mui/material'
import React from 'react'

import { useConfirmation } from '@/app/contexts/confirmation.context'
import { Plan } from '@/app/module/plans/types'
import { useCheckPurchaseAvailable } from '@/app/module/plans/utils'
import { openClearFeed } from '@/app/service/clearfeed/utils'

export enum PlanAction {
  Downgrade = 'downgrade',
  Subscribe = 'subscribe',
  Upgrade = 'upgrade',
  UpgradeToYearly = 'upgrade_to_yearly',
}

type Props = {
  chargeYearly: boolean
  currentPlanBilling?: 'monthly' | 'yearly'
  currentPlanIndex: number
  isOwner: boolean
  plan: Plan
  planIndex: number

  onClick: (plan: Plan, action: PlanAction) => void
}

const SubscribeButton: React.FC<Props> = ({
  chargeYearly,
  currentPlanBilling,
  currentPlanIndex,
  isOwner,
  plan,
  planIndex,
  onClick,
}) => {
  const checkPurchaseAvailable = useCheckPurchaseAvailable()
  const createConfirmation = useConfirmation()

  const isCurrPlanYearly = currentPlanBilling === 'yearly'

  const action = React.useMemo(() => {
    if (currentPlanIndex === -1) {
      return PlanAction.Subscribe
    }
    if (currentPlanIndex === planIndex) {
      return PlanAction.UpgradeToYearly
    }
    if (currentPlanIndex > planIndex) {
      return PlanAction.Downgrade
    }
    return PlanAction.Upgrade
  }, [currentPlanIndex, planIndex])

  const buttonLabel = React.useMemo(() => {
    if (isCurrPlanYearly) {
      return 'Contact Support'
    }

    switch (action) {
      case PlanAction.Downgrade:
        return 'Downgrade Plan'

      case PlanAction.Subscribe:
        return 'Get Started!'

      case PlanAction.UpgradeToYearly:
        return 'Upgrade to yearly'

      default:
        return 'Upgrade Plan'
    }
  }, [action, isCurrPlanYearly])

  const handleClick = React.useCallback(async () => {
    const available = checkPurchaseAvailable(Number(chargeYearly ? plan.priceYearly : plan.priceMonthly))
    if (!available) {
      const confirmed = await createConfirmation({
        confirmButtonLabel: 'Contact Support',
        content:
          'Unfortunately, you do not have sufficient funds and are unable to add credit cards at this time. Please contact our support team for further assistance.',
        hideCancel: true,
        title: 'Purchase Unavailable',
      })

      if (confirmed) {
        openClearFeed()
      }

      return
    }
    onClick(plan, action)
  }, [action, chargeYearly, plan, checkPurchaseAvailable, createConfirmation, onClick])

  const button = React.useMemo(() => {
    switch (action) {
      case PlanAction.Downgrade:
        return (
          <Button disabled={isCurrPlanYearly || !isOwner} onClick={handleClick} fullWidth variant="outlined">
            {buttonLabel}
          </Button>
        )

      case PlanAction.Subscribe:
        return (
          <Button
            disabled={!isOwner || isCurrPlanYearly}
            onClick={handleClick}
            fullWidth
            variant="contained"
            color="primary"
          >
            {buttonLabel}
          </Button>
        )

      case PlanAction.UpgradeToYearly:
        return (
          <Button
            disabled={isCurrPlanYearly || !isOwner || !chargeYearly}
            onClick={handleClick}
            fullWidth
            variant="outlined"
            color="secondary"
          >
            {buttonLabel}
          </Button>
        )

      default:
        return (
          <Button
            disabled={isCurrPlanYearly || !isOwner}
            onClick={handleClick}
            fullWidth
            variant={isCurrPlanYearly ? 'outlined' : 'contained'}
            color="primary"
          >
            {buttonLabel}
          </Button>
        )
    }
  }, [action, buttonLabel, chargeYearly, isCurrPlanYearly, isOwner, handleClick])

  return <>{button}</>
}

export default SubscribeButton

export type { Props as SubscribeButtonProps }
